import { useCallback } from 'react';
import { generatePagedQuery } from 'utils/string.ts';

import useFetch from 'hooks/useFetch';
import useGlobalStore from 'hooks/useGlobalStore/index.ts';

import { AIRCRAFT_API, FETCH_ALL_PAGE_SIZE } from './constants.ts';
import type { PagedResponseDroneResponse } from './types';

const useAircraft = () => {
    const { authenticatedDelete, authenticatedGet } = useFetch();
    const { setValue } = useGlobalStore({
        namespace: 'useAircraft'
    });

    const fetchAircraft = useCallback(
        async (id: string) => {
            const uri = `${AIRCRAFT_API}/${id}`;

            return await authenticatedGet(uri, null);
        },
        [authenticatedGet]
    );

    const fetchAircrafts = useCallback(
        async (
            pageToken?: string | null,
            pageSize = 10,
            hasFlightControllerSerial: boolean = false,
            serialNumbers: string[] = []
        ) => {
            const queryParams = generatePagedQuery(pageToken, pageSize);

            if (serialNumbers.length > 0) {
                queryParams.append('serialNumbers', serialNumbers.join(','));
            }

            let uri = `${AIRCRAFT_API}?${queryParams.toString()}`;

            if (hasFlightControllerSerial) {
                uri += '&hasFlightControllerSerial=true';
            }

            const response = await authenticatedGet(uri, null);

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as PagedResponseDroneResponse;
        },
        [authenticatedGet]
    );

    const fetchAllAircrafts = useCallback(
        async (hasFlightControllerSerial: boolean = false) => {
            try {
                let aircraftResponse = await fetchAircrafts(
                    undefined,
                    FETCH_ALL_PAGE_SIZE,
                    hasFlightControllerSerial
                );
                let aircraft = aircraftResponse.objects;
                let pageToken: string | null | undefined = aircraftResponse.nextPageToken;

                while (aircraftResponse.hasNext) {
                    aircraftResponse = await fetchAircrafts(
                        pageToken,
                        FETCH_ALL_PAGE_SIZE,
                        hasFlightControllerSerial
                    );

                    aircraft = aircraft.concat(aircraftResponse.objects);
                    pageToken = aircraftResponse.nextPageToken;
                }

                setValue({ aircrafts: aircraft });

                return {
                    data: aircraft,
                    error: undefined
                };
            } catch (e) {
                return {
                    data: undefined,
                    error: e
                };
            }
        },
        [fetchAircrafts, setValue]
    );

    const deleteAircraft = useCallback(
        async (id: string) => {
            const uri = `${AIRCRAFT_API}/${id}`;

            const response = await authenticatedDelete(uri, null);

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedDelete]
    );

    return {
        deleteAircraft,
        fetchAircraft,
        fetchAircrafts,
        fetchAllAircrafts
    };
};

export default useAircraft;
